import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type PropsWithChildren,
} from 'react';
import { flushSync } from 'react-dom';
import { authApi, tokenStorage, setTokens, clearTokens } from '../api/client';
import type { AuthUser, LoginPayload } from '../types';

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (payload: LoginPayload) => Promise<AuthUser>;
  signup: (payload: { user_name: string; user_email: string; password: string; phone?: string; shop_name?: string }) => Promise<AuthUser>;
  googleLogin: (idToken: string) => Promise<AuthUser>;
  logout: () => void;
  setUser: (user: AuthUser) => void;
  refreshUser: () => Promise<AuthUser>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const USER_KEY = 'haist_shop_user';

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUserState] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = tokenStorage.get();
    const saved = localStorage.getItem(USER_KEY);
    if (token && saved) {
      try {
        setUserState(JSON.parse(saved));
        setIsLoading(false);
        return;
      } catch {
        tokenStorage.clear();
        localStorage.removeItem(USER_KEY);
      }
    }
    if (token && !saved) {
      // A token was handed off without a cached user — e.g. the extension embed
      // bootstrap, or a fresh tab carrying only the token. Resolve the user from
      // the token instead of forcing a re-login.
      authApi
        .me()
        .then((me) => {
          localStorage.setItem(USER_KEY, JSON.stringify(me));
          setUserState(me);
        })
        .catch(() => {
          clearTokens();
          localStorage.removeItem(USER_KEY);
        })
        .finally(() => setIsLoading(false));
      return;
    }
    setIsLoading(false);
  }, []);

  const _finishLogin = useCallback(async (accessToken: string, refreshToken?: string | null): Promise<AuthUser> => {
    setTokens(accessToken, refreshToken ?? null);
    const me = await authApi.me();
    localStorage.setItem(USER_KEY, JSON.stringify(me));
    flushSync(() => setUserState(me));
    return me;
  }, []);

  const login = useCallback(async (payload: LoginPayload) => {
    const tokens = await authApi.login(payload);
    return _finishLogin(tokens.access_token, tokens.refresh_token);
  }, [_finishLogin]);

  const signup = useCallback(async (payload: { user_name: string; user_email: string; password: string; phone?: string; shop_name?: string }) => {
    const tokens = await authApi.signup(payload);
    return _finishLogin(tokens.access_token, tokens.refresh_token);
  }, [_finishLogin]);

  const googleLogin = useCallback(async (idToken: string) => {
    const tokens = await authApi.googleAuth({ id_token: idToken });
    return _finishLogin(tokens.access_token, tokens.refresh_token);
  }, [_finishLogin]);

  const refreshUser = useCallback(async (): Promise<AuthUser> => {
    const me = await authApi.me();
    localStorage.setItem(USER_KEY, JSON.stringify(me));
    setUserState(me);
    return me;
  }, []);

  const logout = useCallback(() => {
    clearTokens();
    localStorage.removeItem(USER_KEY);
    setUserState(null);
  }, []);

  const setUser = useCallback((updated: AuthUser) => {
    setUserState(updated);
    localStorage.setItem(USER_KEY, JSON.stringify(updated));
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user && !!tokenStorage.get(),
        login,
        signup,
        googleLogin,
        logout,
        setUser,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
